import { useState, useEffect } from "react";
import type { ShareDocument } from "@markeroo/shared";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@/components/Modal";
import { AutoResizeTextarea } from "@/components/AutoResizeTextarea";
import { fieldLabelClass } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { useApiClient } from "@/providers/ApiClientProvider";
import { useNotification } from "@/providers/NotificationProvider";
import type { NotificationKind } from "@/providers/NotificationProvider";
import { notifications, ui } from "@/strings";

interface EditSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: ShareDocument;
  assignmentId: string;
  documentId: string;
  onUpdate: (updated: ShareDocument) => void;
}

const MAX_WORDS = 500;

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export function EditSummaryModal({
  isOpen,
  onClose,
  document,
  assignmentId,
  documentId,
  onUpdate,
}: EditSummaryModalProps) {
  const [celebrate, setCelebrate] = useState(document.summary.celebrate);
  const [priorities, setPriorities] = useState(document.summary.priorities);
  const [strategies, setStrategies] = useState(document.summary.strategies);
  const [isSaving, setIsSaving] = useState(false);

  const api = useApiClient();
  const { show } = useNotification();
  const TOAST_SUCCESS: NotificationKind = "success";
  const TOAST_ERROR: NotificationKind = "error";

  // Sync with document prop changes
  useEffect(() => {
    setCelebrate(document.summary.celebrate);
    setPriorities(document.summary.priorities);
    setStrategies(document.summary.strategies);
  }, [document.summary]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updated = await api.updateDocumentSummary(assignmentId, documentId, {
        celebrate,
        priorities,
        strategies,
      });
      onUpdate(updated);
      show(notifications.summary.updated, TOAST_SUCCESS);
      onClose();
    } catch (error) {
      console.error("Failed to save summary:", error);
      show(notifications.summary.updateFailed, TOAST_ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = (field: "celebrate" | "priorities" | "strategies") => {
    void (async () => {
      try {
        const updated = await api.resetDocumentSummaryField(
          assignmentId,
          documentId,
          field
        );
        // Update local state with reset value
        if (field === "celebrate") setCelebrate(updated.summary.celebrate);
        if (field === "priorities") setPriorities(updated.summary.priorities);
        if (field === "strategies") setStrategies(updated.summary.strategies);
        onUpdate(updated);
        show(notifications.summary.fieldReset(field), TOAST_SUCCESS);
      } catch (error) {
        console.error("Failed to reset:", error);
        show(notifications.summary.resetFailed, TOAST_ERROR);
      }
    })();
  };

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const celebrateWords = countWords(celebrate);
  const prioritiesWords = countWords(priorities);
  const strategiesWords = countWords(strategies);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalHeader title={`${ui.actions.edit} ${ui.labels.summary}`} onClose={handleClose} />
      <ModalBody>
        <div className="flex flex-col gap-6 px-2 pb-4">
          {/* Celebrate Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className={fieldLabelClass}>
                <span className="text-lg font-semibold">
                  {/* eslint-disable-next-line no-restricted-syntax */}
                  <span aria-hidden="true">🎉 </span>{ui.labels.celebrateTitle}
                </span>
                <span className="text-sm text-theme-muted">{ui.labels.celebrateHelp}</span>
              </label>
              <button
                type="button"
                className={buttonVariants.link}
                onClick={() => handleReset("celebrate")}
              >
                {ui.actions.resetToMarkeroo}
              </button>
            </div>
            <AutoResizeTextarea
              value={celebrate}
              onChange={(e) => setCelebrate(e.target.value)}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-base text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              minHeight={96}
              maxHeight={320}
              rows={4}
            />
            <p className={`text-xs ${celebrateWords > MAX_WORDS ? "text-semantic-error" : "text-theme-muted"}`}>
              {celebrateWords} {"/"} {MAX_WORDS} {ui.labels.words}
              {celebrateWords > MAX_WORDS && ` ${ui.labels.exceedsLimitParens}`}
            </p>
          </div>

          {/* Priorities Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className={fieldLabelClass}>
                <span className="text-lg font-semibold">
                  {/* eslint-disable-next-line no-restricted-syntax */}
                  <span aria-hidden="true">🎯 </span>{ui.labels.prioritiesTitle}
                </span>
                <span className="text-sm text-theme-muted">{ui.labels.prioritiesHelp}</span>
              </label>
              <button
                type="button"
                className={buttonVariants.link}
                onClick={() => handleReset("priorities")}
              >
                {ui.actions.resetToMarkeroo}
              </button>
            </div>
            <AutoResizeTextarea
              value={priorities}
              onChange={(e) => setPriorities(e.target.value)}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-base text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              minHeight={96}
              maxHeight={320}
              rows={4}
            />
            <p className={`text-xs ${prioritiesWords > MAX_WORDS ? "text-semantic-error" : "text-theme-muted"}`}>
              {prioritiesWords} {"/"} {MAX_WORDS} {ui.labels.words}
              {prioritiesWords > MAX_WORDS && ` ${ui.labels.exceedsLimitParens}`}
            </p>
          </div>

          {/* Strategies Section */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <label className={fieldLabelClass}>
                <span className="text-lg font-semibold">
                  {/* eslint-disable-next-line no-restricted-syntax */}
                  <span aria-hidden="true">💡 </span>{ui.labels.strategiesTitle}
                </span>
                <span className="text-sm text-theme-muted">{ui.labels.strategiesHelp}</span>
              </label>
              <button
                type="button"
                className={buttonVariants.link}
                onClick={() => handleReset("strategies")}
              >
                {ui.actions.resetToMarkeroo}
              </button>
            </div>
            <AutoResizeTextarea
              value={strategies}
              onChange={(e) => setStrategies(e.target.value)}
              className="w-full rounded-lg border border-border bg-white px-3 py-2 text-base text-theme-primary shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              minHeight={96}
              maxHeight={320}
              rows={4}
            />
            <p className={`text-xs ${strategiesWords > MAX_WORDS ? "text-semantic-error" : "text-theme-muted"}`}>
              {strategiesWords} {"/"} {MAX_WORDS} {ui.labels.words}
              {strategiesWords > MAX_WORDS && ` ${ui.labels.exceedsLimitParens}`}
            </p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className={buttonVariants.secondary}
          onClick={handleClose}
          disabled={isSaving}
        >
          {ui.actions.cancel}
        </button>
        <button
          type="button"
          className={buttonVariants.primary}
          onClick={() => { void handleSave(); }}
          disabled={isSaving}
        >
          {isSaving ? ui.actions.saving : ui.actions.save}
        </button>
      </ModalFooter>
    </Modal>
  );
}
